import { useContext, useEffect, useState, useCallback } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import { CrumbsContext } from 'context/CrumbsContext';
import { Backend } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { NotificationContext } from 'context/NotificationContext';
import { DownloadIcon, EyeIcon, UploadIcon, CogIcon } from '@heroicons/react/outline';
import * as XLSX from 'xlsx';

const ADD_NEW_SENTINEL = '__add_new__';

function Spinner({ className = '' }) {
  return <CogIcon className={`animate-spin h-4 w-4 ${className}`} />;
}

export default function EventsReports() {
  let { setTitle } = useContext(PageTitleContext);
  let { setCrumbs } = useContext(CrumbsContext);
  let { setNotification } = useContext(NotificationContext);

  const [rows, setRows] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({
    title: '',
    description: '',
    location: '',
    report_type: '',
    start_date: '',
    end_date: '',
    file: null,
  });
  const [customLocation, setCustomLocation] = useState('');
  const [customReportType, setCustomReportType] = useState('');
  const [importing, setImporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [venueOptions, setVenueOptions] = useState({
    locations: [],
    reportTypes: [],
  });

  useEffect(() => {
    setTitle('View Venue Monitoring Reports');
    setCrumbs([
      {
        name: 'Events & Reporting',
        link: '/events-manager/view-recording-reports',
      },
    ]);
    fetchReports();
    fetchVenueOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchVenueOptions() {
    Backend.get(API_ROUTES.EVENTS_MANAGER.VENUE_MONITORING_OPTIONS)
      .then((res) => setVenueOptions(res.data))
      .catch(() => {});
  }

  function fetchReports() {
    Promise.allSettled([
      Backend.get(API_ROUTES.EVENTS_MANAGER.REPORTS),
      Backend.get(API_ROUTES.EVENTS_MANAGER.VENUE_SHEETS),
    ]).then(([recordingsRes, sheetsRes]) => {
      const recordings =
        recordingsRes.status === 'fulfilled'
          ? recordingsRes.value.data || []
          : [];
      const sheets =
        sheetsRes.status === 'fulfilled'
          ? sheetsRes.value.data || []
          : [];

      if (
        recordingsRes.status === 'rejected' &&
        sheetsRes.status === 'rejected'
      ) {
        setNotification({
          show: true,
          title: 'Error',
          message: 'Could not load recording reports.',
          type: 'error',
        });
        return;
      }

      const recordingRows = recordings.map((r) => ({
        ...r,
        kind: r.results?.type === 'csv_import' ? 'legacy_csv' : 'recording',
      }));
      const sheetRows = sheets.map((s) => ({
        id: s.id,
        title: s.title,
        description: s.description,
        event: s.location,
        report_type: s.report_type,
        created: s.created,
        start_date: s.start_date,
        end_date: s.end_date,
        kind: 'venue_sheet',
      }));
      const merged = [...recordingRows, ...sheetRows].sort((a, b) => {
        const dateOf = (r) =>
          r.end_date ? new Date(r.end_date).getTime()
          : r.start_date ? new Date(r.start_date).getTime()
          : r.created ? new Date(r.created).getTime()
          : 0;
        return dateOf(b) - dateOf(a);
      });
      setRows(merged);
    }).finally(() => setLoadingReports(false));
  }

  const getReportTypeLabel = useCallback(
    (value) => {
      if (!value) return '—';
      const match = venueOptions.reportTypes.find((rt) => rt.value === value);
      return match ? match.label : value;
    },
    [venueOptions.reportTypes],
  );

  const getLocationLabel = useCallback(
    (value) => {
      if (!value) return '—';
      const match = venueOptions.locations.find((l) => l.value === value);
      return match ? match.label : value;
    },
    [venueOptions.locations],
  );

  function buildAutoTitle(loc, rt, startDate, endDate) {
    const parts = [];
    if (loc && loc !== ADD_NEW_SENTINEL) {
      const match = venueOptions.locations.find((l) => l.value === loc);
      parts.push(match ? match.label : loc);
    }
    if (rt && rt !== ADD_NEW_SENTINEL) {
      const match = venueOptions.reportTypes.find((r) => r.value === rt);
      parts.push(match ? match.label : rt);
    }
    if (startDate && endDate) {
      parts.push(`${startDate} to ${endDate}`);
    }
    return parts.join(' - ');
  }

  function updateAutoTitle(overrides = {}) {
    const merged = { ...importForm, ...overrides };
    const loc =
      merged.location === ADD_NEW_SENTINEL ? customLocation : merged.location;
    const rt =
      merged.report_type === ADD_NEW_SENTINEL
        ? customReportType
        : merged.report_type;
    const auto = buildAutoTitle(
      loc,
      rt,
      merged.start_date,
      merged.end_date,
    );
    if (auto) {
      setImportForm((f) => ({ ...f, ...overrides, title: auto }));
    } else {
      setImportForm((f) => ({ ...f, ...overrides }));
    }
  }

  function extractDatesFromCsv(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return;

      const headerLine = lines[0].toLowerCase();
      const headers = headerLine.split(',').map((h) => h.trim());
      const dateIdx = headers.findIndex(
        (h) => h === 'starttime' || h === 'start_time' || h === 'date',
      );
      if (dateIdx === -1) return;

      let minTime = Infinity;
      let maxTime = -Infinity;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        const val = (cols[dateIdx] || '').trim().replace(/^"|"$/g, '');
        if (!val) continue;
        const t = new Date(val).getTime();
        if (isNaN(t)) continue;
        if (t < minTime) minTime = t;
        if (t > maxTime) maxTime = t;
      }
      if (!isFinite(minTime) || !isFinite(maxTime)) return;

      const fmt = (d) => d.toISOString().split('T')[0];
      const start_date = fmt(new Date(minTime));
      const end_date = fmt(new Date(maxTime));
      updateAutoTitle({ start_date, end_date });
    };
    reader.readAsText(file);
  }

  function handleImportFieldChange(e) {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const f = files[0] || null;
      setImportForm((prev) => ({ ...prev, file: f }));
      if (f) extractDatesFromCsv(f);
    } else if (name === 'location' || name === 'report_type') {
      if (value === ADD_NEW_SENTINEL) {
        setImportForm((prev) => ({ ...prev, [name]: value }));
      } else {
        if (name === 'location') setCustomLocation('');
        if (name === 'report_type') setCustomReportType('');
        updateAutoTitle({ [name]: value });
      }
    } else if (name === 'start_date' || name === 'end_date') {
      updateAutoTitle({ [name]: value });
    } else {
      setImportForm((f) => ({ ...f, [name]: value }));
    }
  }

  function handleImportSubmit(e) {
    e.preventDefault();

    const effectiveLocation =
      importForm.location === ADD_NEW_SENTINEL
        ? customLocation.trim()
        : importForm.location;
    const effectiveReportType =
      importForm.report_type === ADD_NEW_SENTINEL
        ? customReportType.trim()
        : importForm.report_type;

    if (!effectiveLocation) {
      setNotification({
        show: true,
        title: 'Validation',
        message: 'Location is required.',
        type: 'error',
      });
      return;
    }
    if (!effectiveReportType) {
      setNotification({
        show: true,
        title: 'Validation',
        message: 'Report type is required.',
        type: 'error',
      });
      return;
    }
    if (!importForm.file) {
      setNotification({
        show: true,
        title: 'Validation',
        message: 'Please select a CSV file.',
        type: 'error',
      });
      return;
    }

    const data = new FormData();
    data.append('title', importForm.title);
    data.append('description', importForm.description);
    data.append('location', effectiveLocation);
    data.append('report_type', effectiveReportType);
    if (importForm.start_date) data.append('start_date', importForm.start_date);
    if (importForm.end_date) data.append('end_date', importForm.end_date);
    data.append('csv', importForm.file);

    setImporting(true);
    Backend.post(API_ROUTES.EVENTS_MANAGER.VENUE_SHEETS, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(() => {
        setNotification({
          show: true,
          title: 'Success',
          message: 'Venue monitoring sheet imported successfully.',
          type: 'success',
        });
        setImportForm({
          title: '',
          description: '',
          location: '',
          report_type: '',
          start_date: '',
          end_date: '',
          file: null,
        });
        setCustomLocation('');
        setCustomReportType('');
        setShowImport(false);
        fetchReports();
        fetchVenueOptions();
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message || 'Could not import the CSV report.';
        setNotification({
          show: true,
          title: 'Error',
          message: msg,
          type: 'error',
        });
      })
      .finally(() => setImporting(false));
  }

  function openModal(rec) {
    setSelected(rec);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setSelected(null);
  }

  function downloadExcelReport(recording) {
    const results = Array.isArray(recording.results) ? recording.results : [];

    if (results.length === 0) {
      setNotification({
        show: true,
        title: 'No data',
        message: 'No results available for this recording.',
        type: 'error',
      });
      return;
    }

    setDownloadingId(recording.id);
    try {
      const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      };

      const excelData = results.map((track) => ({
        Title: track.title || 'N/A',
        Artist: track.artist || 'N/A',
        Label: track.label || 'N/A',
        Duration: formatDuration(track.duration_ms),
        Genre: track.genre || 'N/A',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 30 },
        { wch: 25 },
        { wch: 30 },
        { wch: 15 },
        { wch: 20 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Results');

      const fileName = `${(recording.title || 'Event-Recording')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } finally {
      setDownloadingId(null);
    }
  }

  function downloadCsvReport(row) {
    setDownloadingId(row.id);
    const url =
      row.kind === 'venue_sheet'
        ? API_ROUTES.EVENTS_MANAGER.VENUE_SHEET_FILE(row.id)
        : API_ROUTES.EVENTS_MANAGER.REPORT_FILE(row.id);
    Backend.get(url, { responseType: 'text' })
      .then((res) => {
        const wb = XLSX.read(res.data, { type: 'string' });
        const fileName = `${(row.title || 'report')
          .replace(/[^a-z0-9]/gi, '_')
          .replace(/_+/g, '_')
          .toLowerCase()}.xlsx`;
        XLSX.writeFile(wb, fileName);
      })
      .catch(() =>
        setNotification({
          show: true,
          title: 'Error',
          message: 'Could not download the report file.',
          type: 'error',
        }),
      )
      .finally(() => setDownloadingId(null));
  }

  const selectClass =
    'block w-full border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white';
  const inputClass =
    'block w-full border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  return (
    <div className="min-h-full px-5 py-4 mt-0">
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg leading-6 font-semibold text-gray-900">
            Processed Uploads
          </h2>
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <UploadIcon className="h-4 w-4 mr-1" />
            {showImport ? 'Cancel Import' : 'Import CSV Report'}
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          View and export reports for all event recordings that have completed
          processing.
        </p>

        {showImport && (
          <div className="mb-6 border border-indigo-200 rounded-lg p-4 bg-indigo-50">
            <h3 className="text-sm font-semibold text-indigo-900 mb-3">
              Import Venue Monitoring CSV
            </h3>
            <form
              onSubmit={handleImportSubmit}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {/* Location */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="location"
                  value={importForm.location}
                  onChange={handleImportFieldChange}
                  className={selectClass}
                >
                  <option value="">Select location...</option>
                  {venueOptions.locations.map((loc) => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                  <option value={ADD_NEW_SENTINEL}>+ Add new location</option>
                </select>
                {importForm.location === ADD_NEW_SENTINEL && (
                  <input
                    type="text"
                    placeholder="Enter new location name"
                    value={customLocation}
                    onChange={(e) => {
                      setCustomLocation(e.target.value);
                      const auto = buildAutoTitle(
                        e.target.value,
                        importForm.report_type === ADD_NEW_SENTINEL
                          ? customReportType
                          : importForm.report_type,
                        importForm.start_date,
                        importForm.end_date,
                      );
                      if (auto) setImportForm((f) => ({ ...f, title: auto }));
                    }}
                    className={`${inputClass} mt-1`}
                  />
                )}
              </div>

              {/* Report Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Report Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="report_type"
                  value={importForm.report_type}
                  onChange={handleImportFieldChange}
                  className={selectClass}
                >
                  <option value="">Select report type...</option>
                  {venueOptions.reportTypes.map((rt) => (
                    <option key={rt.value} value={rt.value}>
                      {rt.label}
                    </option>
                  ))}
                  <option value={ADD_NEW_SENTINEL}>
                    + Add new report type
                  </option>
                </select>
                {importForm.report_type === ADD_NEW_SENTINEL && (
                  <input
                    type="text"
                    placeholder="Enter new report type"
                    value={customReportType}
                    onChange={(e) => {
                      setCustomReportType(e.target.value);
                      const auto = buildAutoTitle(
                        importForm.location === ADD_NEW_SENTINEL
                          ? customLocation
                          : importForm.location,
                        e.target.value,
                        importForm.start_date,
                        importForm.end_date,
                      );
                      if (auto) setImportForm((f) => ({ ...f, title: auto }));
                    }}
                    className={`${inputClass} mt-1`}
                  />
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={importForm.start_date}
                  onChange={handleImportFieldChange}
                  className={inputClass}
                />
                <p className="text-xs text-gray-400 mt-0.5">
                  Auto-detected from CSV when available
                </p>
              </div>

              {/* End Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={importForm.end_date}
                  onChange={handleImportFieldChange}
                  className={inputClass}
                />
                <p className="text-xs text-gray-400 mt-0.5">
                  Auto-detected from CSV when available
                </p>
              </div>

              {/* Title (auto-populated, editable) */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={importForm.title}
                  onChange={handleImportFieldChange}
                  placeholder="Auto-generated from selections above"
                  className={inputClass}
                />
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  name="description"
                  value={importForm.description}
                  onChange={handleImportFieldChange}
                  className={inputClass}
                />
              </div>

              {/* CSV File */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  CSV File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  name="file"
                  accept=".csv"
                  onChange={handleImportFieldChange}
                  className="block w-full text-sm text-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200"
                />
              </div>

              {/* Buttons */}
              <div className="sm:col-span-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowImport(false)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={importing}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {importing && <Spinner className="mr-1.5" />}
                  {importing ? 'Importing…' : 'Import'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700">
                  Title
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">
                  Report Type
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">
                  Location
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">
                  Report Date
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((rec) => {
                const fmtDate = (d) => {
                  if (!d) return '';
                  const dt = new Date(d);
                  const dd = String(dt.getDate()).padStart(2, '0');
                  const mm = String(dt.getMonth() + 1).padStart(2, '0');
                  const yyyy = dt.getFullYear();
                  return `${dd}/${mm}/${yyyy}`;
                };
                const reportDate =
                  rec.kind === 'venue_sheet' && rec.start_date && rec.end_date
                    ? `${fmtDate(rec.start_date)} – ${fmtDate(rec.end_date)}`
                    : rec.created
                    ? fmtDate(rec.created)
                    : '';
                const isCsv =
                  rec.kind === 'venue_sheet' || rec.kind === 'legacy_csv';
                return (
                  <tr key={`${rec.kind}-${rec.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {rec.title}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {rec.kind === 'venue_sheet'
                        ? getReportTypeLabel(rec.report_type)
                        : rec.kind === 'recording'
                        ? 'Event Recording'
                        : '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {rec.kind === 'venue_sheet'
                        ? getLocationLabel(rec.event)
                        : rec.event || '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {reportDate}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right space-x-2">
                      {isCsv ? (
                        <button
                          type="button"
                          disabled={downloadingId === rec.id}
                          onClick={() => downloadCsvReport(rec)}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs rounded-md shadow-sm text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50"
                        >
                          {downloadingId === rec.id ? (
                            <Spinner className="mr-1" />
                          ) : (
                            <DownloadIcon className="h-4 w-4 mr-1" />
                          )}
                          {downloadingId === rec.id ? 'Downloading…' : 'Download'}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            disabled={downloadingId === rec.id}
                            onClick={() => downloadExcelReport(rec)}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs rounded-md shadow-sm text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50"
                          >
                            {downloadingId === rec.id ? (
                              <Spinner className="mr-1" />
                            ) : (
                              <DownloadIcon className="h-4 w-4 mr-1" />
                            )}
                            {downloadingId === rec.id ? 'Downloading…' : 'Download'}
                          </button>
                          <button
                            type="button"
                            onClick={() => openModal(rec)}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs rounded-md shadow-sm text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View Results
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {loadingReports && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <CogIcon className="animate-spin h-5 w-5 text-indigo-500" />
                      <span>Loading reports…</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loadingReports && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-4 text-center text-gray-500"
                  >
                    No processed uploads found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && selected && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen text-center">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={closeModal}
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Results for {selected.title}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">
                              Title
                            </th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">
                              Artist
                            </th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">
                              Label
                            </th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">
                              Duration
                            </th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">
                              Genre
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(Array.isArray(selected.results)
                            ? selected.results
                            : []
                          ).map((track, idx) => {
                            const formatDuration = (ms) => {
                              if (!ms) return 'N/A';
                              const totalSeconds = Math.floor(ms / 1000);
                              const minutes = Math.floor(totalSeconds / 60);
                              const seconds = totalSeconds % 60;
                              return `${minutes}:${seconds
                                .toString()
                                .padStart(2, '0')}`;
                            };

                            return (
                              <tr key={idx}>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {track.title || 'N/A'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {track.artist || 'N/A'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {track.label || 'N/A'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {formatDuration(track.duration_ms)}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {track.genre || 'N/A'}
                                </td>
                              </tr>
                            );
                          })}
                          {(!selected.results ||
                            !Array.isArray(selected.results) ||
                            selected.results.length === 0) && (
                            <tr>
                              <td
                                colSpan={5}
                                className="px-4 py-4 text-center text-gray-500"
                              >
                                No results available for this recording yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
